import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X, Wallet } from "lucide-react";

/**
 * BudgetAlertBanner
 *
 * Renders a persistent warning banner at the top of the Dashboard when one or
 * more category budgets / card caps are at ≥80% of their monthly limit. Mirrors
 * the Due Soon banner pattern (dismissible per session, red for over-budget,
 * amber for at-risk).
 *
 * Props:
 *  - rows: `[{scope, label, category, card_name?, monthly_limit, spent, pct, over, at_risk, currency}]`
 *  - onDismiss: fn
 */
const BudgetAlertBanner = ({ rows = [], onDismiss }) => {
  if (!rows || rows.length === 0) return null;
  const anyOver = rows.some((r) => r.over);
  const tone = anyOver ? "danger" : "warning";
  const color = tone === "danger" ? "#FF5252" : "#F5B841";
  const bg = tone === "danger" ? "rgba(255,82,82,0.06)" : "rgba(245,184,65,0.06)";
  const border = tone === "danger" ? "rgba(255,82,82,0.35)" : "rgba(245,184,65,0.35)";
  const titleText = anyOver
    ? rows.length === 1
      ? "You're over budget"
      : `${rows.length} budgets over the limit`
    : rows.length === 1
    ? "You're close to your budget"
    : `${rows.length} budgets nearing the limit`;

  return (
    <AnimatePresence>
      <motion.section
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.35 }}
        className="mt-6 rounded-2xl border px-4 sm:px-5 py-4"
        style={{ background: bg, borderColor: border }}
        data-testid="budget-alert-banner"
      >
        <div className="flex items-start gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${color}18`, border: `1px solid ${color}55`, color }}
          >
            {anyOver ? <AlertTriangle size={16} /> : <Wallet size={16} />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div
                  className="text-[10px] uppercase tracking-[0.22em] font-bold"
                  style={{ color }}
                >
                  {tone === "danger" ? "Over budget" : "Budget nudge"}
                </div>
                <h3
                  className="text-base sm:text-lg font-semibold text-white mt-0.5 truncate"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  data-testid="budget-alert-banner-title"
                >
                  {titleText}
                </h3>
              </div>
              {onDismiss && (
                <button
                  type="button"
                  onClick={onDismiss}
                  aria-label="Dismiss budget banner"
                  data-testid="budget-alert-banner-dismiss"
                  className="text-slate-400 hover:text-white transition-colors -mr-1 -mt-1 p-1"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <ul className="mt-3 space-y-1.5" data-testid="budget-alert-banner-list">
              {rows.slice(0, 4).map((r) => {
                const rowColor = r.over ? "#FF5252" : "#F5B841";
                const key = r.scope === "card" ? `card-${r.card_id}` : `cat-${r.category}`;
                const primary =
                  r.scope === "card"
                    ? `${r.label} on ${r.card_name}`
                    : `${r.label} budget`;
                return (
                  <li
                    key={key}
                    className="flex items-center justify-between gap-3 text-xs"
                    data-testid={`budget-alert-row-${key}`}
                  >
                    <span className="text-slate-200 truncate">
                      <span className="font-semibold">{primary}</span>
                      <span className="text-slate-400"> · {r.currency} {r.spent.toFixed(0)} of {r.currency} {r.monthly_limit.toFixed(0)}</span>
                    </span>
                    <span className="font-bold whitespace-nowrap" style={{ color: rowColor }}>
                      {r.pct.toFixed(0)}%
                    </span>
                  </li>
                );
              })}
              {rows.length > 4 && (
                <li className="text-[11px] text-slate-400">
                  + {rows.length - 4} more · manage in Settings → Budgets
                </li>
              )}
            </ul>
          </div>
        </div>
      </motion.section>
    </AnimatePresence>
  );
};

export default BudgetAlertBanner;
